import type { Permission } from '@yana/shared';

/**
 * Разделы приложения и право, без которого раздел не существует.
 *
 * Список один на меню и на маршруты — и это главное в этом файле.
 * Пока они жили порознь, пункт «Сотрудники» пропадал из шапки у рекрутёра,
 * но адрес `/users` открывался: страница рисовалась, запрос уходил, сервер
 * отвечал «недостаточно прав», и человек оставался на пустом экране с
 * ошибкой. Данные при этом не утекали — их закрывает сервер, — но выглядит
 * это как дыра, и объяснять заказчику, что «на самом деле всё хорошо»,
 * приходится каждый раз заново.
 *
 * Теперь право написано один раз: шапка по нему прячет пункт, роутер по
 * нему же разворачивает на таблицу.
 */
export interface NavSection {
  /** Имя маршрута — по нему роутер и находит право */
  name: string;
  to: string;
  label: string;
  /** Нет права — нет ни пункта в меню, ни доступа по адресу */
  permission?: Permission;
  /**
   * Раздел раскрывается списком, а не ведёт по своему адресу.
   *
   * Так устроены листы: их состав заводит заказчик из интерфейса (Р-22),
   * перечислить их здесь нельзя. Право всё равно записано в этом списке —
   * иначе роутер не узнал бы, что адрес закрыт для роли.
   */
  dynamic?: boolean;
}

export const SECTIONS: readonly NavSection[] = [
  { name: 'grid', to: '/', label: 'Таблица' },
  // Остальные вкладки рабочей книги. Что именно в списке — приходит
  // с сервера: лист, закрытый для роли, не появляется ни в меню, ни по адресу
  { name: 'sheet', to: '/sheets', label: 'Листы', permission: 'sheet.view', dynamic: true },
  // Отчёты — картина по всей компании, а не по своим кандидатам:
  // воронка, выработка по рекрутёрам, разбивка по городам
  { name: 'dashboard', to: '/dashboard', label: 'Отчёты', permission: 'report.view' },
  { name: 'blacklist', to: '/blacklist', label: 'Чёрный список', permission: 'blacklist.view' },
  // Страница объединяет загрузку и выгрузку: право на выгрузку есть
  // у рекрутёра, право на загрузку — только у администратора, и внутри
  // страницы вкладка «Импорт» появляется отдельной проверкой
  { name: 'import', to: '/import', label: 'Импорт и выгрузка', permission: 'data.export' },
  // Рассылка — та же выгрузка данных, только в один столбец с номерами
  { name: 'mailing', to: '/mailing', label: 'Рассылка', permission: 'data.export' },
  /*
    Личную цветовую схему настраивает каждый пользователь, поэтому вход
    открыт по `dictionary.view`, которое есть у всех ролей.

    Права на общие данные остаются внутри раздела: справочники меняет
    `dictionary.edit`, общие настройки — `settings.manage`. Доступ к своей
    палитре не даёт права менять оформление таблицы для всей компании.
  */
  { name: 'settings', to: '/settings', label: 'Настройки', permission: 'dictionary.view' },
  { name: 'users', to: '/users', label: 'Сотрудники', permission: 'user.view' },
  { name: 'journal', to: '/journal', label: 'Журнал', permission: 'audit.view' },
];

/** Право раздела по имени маршрута. */
export function permissionFor(name: string): Permission | undefined {
  return SECTIONS.find((s) => s.name === name)?.permission;
}
